import {ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute, Params} from '@angular/router';
import {HttpClient} from '@angular/common/http';
import {forkJoin, Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {PageLinkFilterLabel} from '../page-link-filter-label';
import {PageLinkModel} from '../page-link.model';

@Component({
  selector: 'in-page-links-list',
  templateUrl: './page-links-list.component.html',
  styleUrls: ['./page-links-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PageLinksListComponent implements OnInit, OnChanges {
  @Input() pageLinks: PageLinkModel[] = [];
  @Input() resolve: Params = {};
  @Input() isCount = false;
  @Input() filterLabels: PageLinkFilterLabel[] = [];

  bindFilterLabels = this.filterByLabels.bind(this);

  constructor(private route: ActivatedRoute, private http: HttpClient) {
  }

  ngOnInit(): void {
    return;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateLinksCount();
  }

  updateLinksCount(): void {
    if (!this.isCount) {
      return;
    }

    this.pageLinks.forEach(link => {
      const countSource = link.countUrl;
      if (typeof countSource === 'number') {
        link.count = of(countSource);
        return;
      }

      if (Array.isArray(countSource)) {
        const requests: Observable<number>[] = countSource.map(url =>
          this.http.get<unknown>(this.resolveUrl(url)).pipe(
            map(result => this.getResultCount(result))
          )
        );
        link.count = requests.length > 0
          ? forkJoin(requests).pipe(map((counts: number[]) => counts.reduce((total: number, count: number) => total + count, 0)))
          : of(0);
        return;
      }

      if (typeof countSource === 'string' && countSource.length > 0) {
        link.count = this.http.get<unknown>(this.resolveUrl(countSource)).pipe(
          map(result => this.getResultCount(result))
        );
      }
    });
  }

  resolveUrl(url: string, status = {result: 'ok'}): string {
    let resolvedUrl = url;
    Object.keys(this.resolve).forEach(key => {
      if (this.resolve[key]) {
        resolvedUrl = resolvedUrl.replace(`:${key}`, this.resolve[key]);
      }
    });
    Object.keys(this.route.snapshot.params).forEach(key => {
      if (this.route.snapshot.params[key]) {
        resolvedUrl = resolvedUrl.replace(`:${key}`, this.route.snapshot.params[key]);
      }
    });
    Object.keys(this.route.parent?.snapshot.params || {}).forEach(key => {
      resolvedUrl = resolvedUrl.replace(`:${key}`, this.route.parent.snapshot.params[key]);
    });

    const notResolvedParams = Object.keys(this.resolve).filter(key => resolvedUrl.indexOf(`:${key}`) >= 0);
    if (notResolvedParams.length > 0 && (notResolvedParams[0] !== 'controlprocedureId' && notResolvedParams.length === 1)) {
      status.result = 'error';
    }
    return resolvedUrl;
  }

  resolveQueryParams(params: {}): {} {
    if (params) {
      const resolvedparams = {};
      Object.keys(this.resolve).forEach(key => {
        if (this.resolve[key] && params[key]) {
          resolvedparams[params[key]] = this.resolve[key];
        }
      });
      return resolvedparams;
    }
  }

  filterByLabels(data): boolean {
    const status = {result: 'ok'};
    this.resolveUrl(data.url, status);
    if (status.result === 'error') {
      return false;
    }
    return (this.filterLabels.find(filter => filter.label === data.label) || {label: 'any', isShow: true}).isShow;
  }

  private getResultCount(result: unknown): number {
    if (typeof result === 'number') {
      return result;
    }
    if (Array.isArray(result)) {
      return result.length;
    }
    return 0;
  }
}
